import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { MultCoefficient1ActivityDataModel, generateMultCoefficient1ActivityData } from './MultCoefficient1.utils'

interface UseMultCoefficient1ReturnType {
  integers: number[]
  coefficient: number
  variable: string
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseMultCoefficient1Props {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseMultCoefficient1({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseMultCoefficient1Props): UseMultCoefficient1ReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [activityData, setActivityData] = useState<MultCoefficient1ActivityDataModel>({
    answer: 0,
    integers: [],
    coefficient: 1,
    variable: '',
    equationString: '',
    maxHistoryLength: 3,
  })
  const [answer, setAnswer] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateMultCoefficient1ActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(parseFloat(answer) === parseFloat(activityData.answer.toString()))
  }, [onAnswerStateChange, answer, activityData.answer])

  const onInputChange = useCallback(
    (value: string) => {
      if (value === 'clear') {
        setAnswer('')
        onAnswerStateChange(null)
      } else {
        setAnswer(value)
      }
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [activityData.equationString, ...prevState].slice(0, activityData.maxHistoryLength))
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.equationString, activityData.maxHistoryLength])

  return {
    integers: activityData.integers,
    coefficient: activityData.coefficient,
    variable: activityData.variable,
    answer,
    formValid: answer !== '',
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseMultCoefficient1
